import { useCallback, useEffect, useMemo, useState } from 'react';
import { PROJECT_ID, GET_CONTACT_INQUIRIES_URL } from '@/lib/supabase';
import type { ContactInquiry } from '@/lib/types';
import { formatDateTime } from '@/lib/format';

interface ParsedMessage {
  isJson: boolean;
  entries: { key: string; value: string }[];
  text: string;
}

function extractInquiries(json: unknown): ContactInquiry[] {
  if (Array.isArray(json)) return json as ContactInquiry[];
  if (json && typeof json === 'object') {
    const obj = json as Record<string, unknown>;
    const keys = ['data', 'inquiries', 'result', 'results', 'items', 'rows'];
    for (const key of keys) {
      if (Array.isArray(obj[key])) return obj[key] as ContactInquiry[];
    }
  }
  return [];
}

function parseMessage(raw: unknown): ParsedMessage {
  if (raw == null) return { isJson: false, entries: [], text: '' };
  let value: unknown = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        return { isJson: false, entries: [], text: value as string };
      }
    } else {
      return { isJson: false, entries: [], text: value };
    }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, v]) => ({
      key,
      value: v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v),
    }));
    return { isJson: true, entries, text: entries.map((e) => `${e.key}: ${e.value}`).join(' / ') };
  }
  return {
    isJson: false,
    entries: [],
    text: typeof value === 'string' ? value : JSON.stringify(value),
  };
}

function resolveEmail(item: ContactInquiry, parsed: ParsedMessage): string {
  const direct = typeof item.email === 'string' ? item.email : '';
  if (direct) return direct;
  const fromEntries = parsed.entries.find((e) => e.key === 'メールアドレス' || e.key === 'email');
  return fromEntries?.value || '';
}

export default function InquiriesTab() {
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<ContactInquiry | null>(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(GET_CONTACT_INQUIRIES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: PROJECT_ID }),
      });
      const text = await res.text();
      let json: unknown = null;
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
      if (!res.ok) {
        const message =
          (json && typeof json === 'object' && ('error' in json || 'message' in json)
            ? String(
                (json as Record<string, unknown>).error ||
                  (json as Record<string, unknown>).message,
              )
            : '') || '問い合わせ履歴の取得に失敗しました。';
        throw new Error(message);
      }
      const rows = extractInquiries(json);
      const sorted = [...rows].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setInquiries(sorted);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : '問い合わせ履歴の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const rows = useMemo(
    () =>
      inquiries.map((item, idx) => {
        const parsed = parseMessage(item.message);
        return {
          key: item.id != null ? String(item.id) : `inquiry-${idx}`,
          item,
          parsed,
          email: resolveEmail(item, parsed),
        };
      }),
    [inquiries],
  );

  const selectedParsed = useMemo(
    () => (selected ? parseMessage(selected.message) : null),
    [selected],
  );

  if (loading) {
    return (
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
        <i className="ri-loader-4-line animate-spin text-2xl"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-12 text-center">
        <p className="text-[#e8e8f0]">{error}</p>
        <button
          type="button"
          onClick={fetchInquiries}
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          再読み込み
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center">
        <i className="ri-mail-open-line text-4xl text-[#7a7a8a]"></i>
        <p className="mt-4 text-[#a0a0b0]">問い合わせはまだありません。</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#2a2a3e] bg-[#12121f]">
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase whitespace-nowrap">受信日時</th>
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">メールアドレス</th>
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">お問い合わせ内容</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, item, parsed, email }) => (
                <tr
                  key={key}
                  onClick={() => setSelected(item)}
                  className="border-b border-[#2a2a3e] hover:bg-[#12121f] transition-colors cursor-pointer"
                >
                  <td className="px-5 py-4 text-sm text-[#a0a0b0] whitespace-nowrap">
                    {formatDateTime(item.created_at) || '-'}
                  </td>
                  <td className="px-5 py-4 text-sm text-white whitespace-nowrap">{email || '-'}</td>
                  <td className="px-5 py-4 text-sm text-[#c8c8d8] max-w-md">
                    <span className="line-clamp-2">{parsed.text || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && selectedParsed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)}></div>
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-base font-extrabold text-white">お問い合わせ詳細</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#2a2a3e] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <dl className="mt-5 space-y-3">
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                <dt className="w-40 shrink-0 text-xs font-semibold text-[#a78bfa] uppercase">受信日時</dt>
                <dd className="text-sm text-white">{formatDateTime(selected.created_at) || '-'}</dd>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                <dt className="w-40 shrink-0 text-xs font-semibold text-[#a78bfa] uppercase">メールアドレス</dt>
                <dd className="text-sm text-white break-all">
                  {resolveEmail(selected, selectedParsed) || '-'}
                </dd>
              </div>
            </dl>

            <div className="mt-6 border-t border-[#2a2a3e] pt-5">
              <p className="text-xs font-semibold text-[#a78bfa] uppercase mb-3">お問い合わせ内容</p>
              {selectedParsed.isJson && selectedParsed.entries.length > 0 ? (
                <div className="space-y-3">
                  {selectedParsed.entries.map((entry) => (
                    <div key={entry.key} className="rounded-xl bg-[#12121f] border border-[#2a2a3e] p-4">
                      <p className="text-xs font-semibold text-[#a0a0b0]">{entry.key}</p>
                      <p className="mt-1 text-sm text-white whitespace-pre-wrap break-words">
                        {entry.value || '-'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white whitespace-pre-wrap break-words">
                  {selectedParsed.text || '-'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}